export type DealWinSalesStage =
  | "lead"
  | "qualified"
  | "discovery"
  | "proposal"
  | "negotiation"
  | "verbal_commit"
  | "won"
  | "lost";

export type DealWinDealStatus = "open" | "won" | "lost" | "disqualified" | "archived";
export type DealWinRiskLevel = "low" | "medium" | "high" | "critical";
export type DealWinConfidenceLevel = "low" | "medium" | "high";
export type DealWinActionPriority = "critical" | "high" | "medium" | "low";

export type DealWinContactProfileSource = "manual" | "crm" | "inferred";
export type DealWinContactProfileConfidence = "low" | "medium" | "high";
export type DealWinDecisionStyle =
  | "analytical"
  | "relationship_driven"
  | "authority_driven"
  | "innovation_driven"
  | "risk_averse"
  | "speed_oriented"
  | "consensus_driven";
export type DealWinCommunicationPreference =
  | "numbers_and_facts"
  | "strategic_vision"
  | "operational_detail"
  | "trust_and_relationship"
  | "proof_and_references"
  | "concise_executive_summary";
export type DealWinContactRiskTrigger =
  | "unclear_roi"
  | "implementation_effort"
  | "internal_politics"
  | "budget_uncertainty"
  | "loss_of_control"
  | "reputational_risk"
  | "missing_references"
  | "unclear_decision_path";
export type DealWinProofPreference =
  | "business_case"
  | "customer_reference"
  | "roi_calculation"
  | "implementation_plan"
  | "executive_summary"
  | "technical_validation"
  | "pilot_or_test";
export type DealWinContactRecommendedApproach =
  | "lead_with_business_case"
  | "reduce_perceived_risk"
  | "secure_internal_sponsor"
  | "show_reference_or_proof"
  | "clarify_decision_path"
  | "create_executive_summary"
  | "align_on_next_decision";

export interface DealWinContactDecisionProfile {
  version: "1.0";
  source: DealWinContactProfileSource;
  confidence: DealWinContactProfileConfidence;
  decisionStyle?: DealWinDecisionStyle;
  communicationPreference?: DealWinCommunicationPreference;
  riskTriggers: DealWinContactRiskTrigger[];
  proofPreference: DealWinProofPreference[];
  recommendedApproach: DealWinContactRecommendedApproach[];
  notes?: string;
}

export interface DealWinHauffeValueContext {
  source?: string;
  version?: string;
  createdAt?: string;
  modelId?: string;
  modelStatus?: string;
  modelOnly?: boolean;
  criteriaCount?: number;
  segmentCount?: number;
  customerValueScore?: number;
  customerFitScore?: number;
  strategicFitScore?: number;
  commercialFitScore?: number;
  profitabilityScore?: number;
  retentionPotentialScore?: number;
  expansionPotentialScore?: number;
  serviceEffortScore?: number;
  segment?: string;
  fitCategory?: string;
  summary?: string;
  assessedAt?: string;
  marginPotentialScore?: number;
  positiveFactors?: string[];
  negativeFactors?: string[];
  contactDecisionProfile?: DealWinContactDecisionProfile;
}

export interface DealWinDeal {
  id: string;
  title: string;
  companyName: string;
  dealValue: number;
  currency: string;
  salesStage: DealWinSalesStage;
  expectedCloseDate: string;
  ownerName?: string;
  status: DealWinDealStatus;
  contactName?: string;
  contactEmail?: string;
  industry?: string;
  companySize?: string;
  buyingCenterKnown?: boolean;
  decisionMakerAccess?: string;
  budgetConfirmed?: boolean;
  painConfirmed?: boolean;
  urgencyLevel?: "low" | "medium" | "high";
  competitorKnown?: boolean;
  offerSentAt?: string;
  lastActivityAt?: string;
  nextStep?: string;
  notes?: string;
  hauffeValueContext?: DealWinHauffeValueContext;
  createdAt: string;
  updatedAt: string;
}

export interface DealWinScoreBreakdownItem {
  key?: string;
  label?: string;
  dimension: string;
  score: number;
  weight: number;
  weightedScore?: number;
  contribution: number;
  reasons?: string[];
}

export interface DealWinRiskReason {
  riskKey?: string;
  riskTitle?: string;
  riskDescription?: string;
  severity: DealWinRiskLevel;
  affectedScoreDimension?: string;
  suggestedAction?: string;
}

export interface DealWinRecommendedAction {
  actionKey?: string;
  title: string;
  description: string;
  priority?: DealWinActionPriority;
  relatedRiskKey?: string;
  affectedScoreDimension?: string;
}

export interface DealWinImprovementLever {
  leverKey: string;
  title: string;
  description: string;
  currentState: string;
  targetState: string;
  estimatedScoreImpact: number;
  estimatedWinProbabilityImpact: number;
  priority: DealWinActionPriority;
  affectedScoreDimension: string;
  relatedRiskKey?: string;
  suggestedAction: string;
}

export interface DealWinAssessment {
  id: string;
  dealId: string;
  winProbability: number;
  scoreTotal: number;
  scoreBreakdown: DealWinScoreBreakdownItem[];
  riskLevel: DealWinRiskLevel;
  riskReasons: DealWinRiskReason[];
  recommendedActions: DealWinRecommendedAction[];
  improvementLevers: DealWinImprovementLever[];
  confidenceLevel: DealWinConfidenceLevel;
  assessmentVersion: string;
  inputSnapshot?: {
    deal?: Record<string, unknown>;
    hauffeValueContext?: DealWinHauffeValueContext;
    source?: string;
  };
  createdAt: string;
}

export type DealWinDealInput = {
  title: string;
  companyName: string;
  dealValue: number;
  currency: string;
  salesStage: DealWinSalesStage;
  expectedCloseDate: string;
  ownerName?: string;
  status: DealWinDealStatus;
  contactName?: string;
  contactEmail?: string;
  industry?: string;
  companySize?: string;
  buyingCenterKnown?: boolean;
  decisionMakerAccess?: string;
  budgetConfirmed?: boolean;
  painConfirmed?: boolean;
  urgencyLevel?: "low" | "medium" | "high";
  competitorKnown?: boolean;
  offerSentAt?: string;
  lastActivityAt?: string;
  nextStep?: string;
  notes?: string;
  hauffeValueContext?: DealWinHauffeValueContext;
};

async function parseJson<T>(response: Response): Promise<T> {
  return (await response.json()) as T;
}

async function assertOk(response: Response): Promise<void> {
  if (response.ok) return;
  const data = await response.json().catch(() => undefined) as { error?: string; message?: string } | undefined;
  throw new Error(data?.message ?? data?.error ?? "Deal Win Engine data could not be loaded.");
}

export async function fetchDealWinDeals(): Promise<DealWinDeal[]> {
  const response = await fetch("/api/deal-win-engine/deals", { credentials: "include" });
  await assertOk(response);
  const data = await parseJson<{ deals: DealWinDeal[] }>(response);
  return data.deals;
}

export async function createDealWinDeal(input: DealWinDealInput): Promise<DealWinDeal> {
  const response = await fetch("/api/deal-win-engine/deals", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input)
  });
  await assertOk(response);
  const data = await parseJson<{ deal: DealWinDeal }>(response);
  return data.deal;
}

export async function fetchDealWinDeal(dealId: string): Promise<DealWinDeal> {
  const response = await fetch(`/api/deal-win-engine/deals/${encodeURIComponent(dealId)}`, { credentials: "include" });
  await assertOk(response);
  const data = await parseJson<{ deal: DealWinDeal }>(response);
  return data.deal;
}

export async function updateDealWinDeal(dealId: string, input: DealWinDealInput): Promise<DealWinDeal> {
  const response = await fetch(`/api/deal-win-engine/deals/${encodeURIComponent(dealId)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input)
  });
  await assertOk(response);
  const data = await parseJson<{ deal: DealWinDeal }>(response);
  return data.deal;
}

export async function archiveDealWinDeal(dealId: string): Promise<void> {
  const response = await fetch(`/api/deal-win-engine/deals/${encodeURIComponent(dealId)}`, {
    method: "DELETE",
    credentials: "include"
  });
  await assertOk(response);
}

export async function assessDealWinDeal(dealId: string): Promise<DealWinAssessment> {
  const response = await fetch(`/api/deal-win-engine/deals/${encodeURIComponent(dealId)}/assess`, {
    method: "POST",
    credentials: "include"
  });
  await assertOk(response);
  const data = await parseJson<{ assessment: DealWinAssessment }>(response);
  return data.assessment;
}

export async function createDealWinCustomerValueSnapshot(dealId: string): Promise<{
  deal: DealWinDeal;
  snapshot: DealWinHauffeValueContext;
}> {
  const response = await fetch(`/api/deal-win-engine/deals/${encodeURIComponent(dealId)}/customer-value-snapshot`, {
    method: "POST",
    credentials: "include"
  });
  await assertOk(response);
  return parseJson<{ deal: DealWinDeal; snapshot: DealWinHauffeValueContext }>(response);
}

export async function fetchLatestDealWinAssessment(dealId: string): Promise<DealWinAssessment | undefined> {
  const response = await fetch(`/api/deal-win-engine/deals/${encodeURIComponent(dealId)}/latest-assessment`, {
    credentials: "include"
  });
  if (response.status === 404) return undefined;
  await assertOk(response);
  const data = await parseJson<{ assessment: DealWinAssessment }>(response);
  return data.assessment;
}

export async function fetchDealWinAssessments(dealId: string): Promise<DealWinAssessment[]> {
  const response = await fetch(`/api/deal-win-engine/deals/${encodeURIComponent(dealId)}/assessments?limit=5`, {
    credentials: "include"
  });
  await assertOk(response);
  const data = await parseJson<{ assessments: DealWinAssessment[] }>(response);
  return data.assessments;
}
